import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TableFunction from '../../../../../components/table/TableFunction';
import { useTranslation } from 'react-i18next';
import {
    GetGeofenceList,
    UpdateGeofenceHierarchyLevel,
    UpdateGeofenceStatus
} from "../../../../../reduxToolkit/FineGeofence/FineGeofenceApi";
import { useDispatch, useSelector } from "react-redux";
import { GEOFENCES_MODEL } from "../Model/Model";
import {
    DEFAULT_LATITUDE,
    DEFAULT_LONGITUDE,
    errorMsg,
    filterByName,
    tryGetAddress
} from "../../../../../Apis/functions";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import { DeleteIconsOptions, DetailsIconsOptions, IconOptions, UpdateIconsOptions } from "../../../../../components/buttons/IconButtons";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import routes from "../../../../../Routes/routes";
import { STATUS_MODEL } from "../../../../../enums/statusEnum";
import { toast } from "react-toastify";
import { resetGetGeofenceList } from "../../../../../reduxToolkit/FineGeofence/FineGeofenceSlice";
import { DeleteItemsApi } from "../../../../../reduxToolkit/Commons/CommonsApi";
import { TABLES } from "../../../../../Apis/Tables";
import {TABS_TRAFFIC, TRAFFIC_SUB_VIEW_MODEL} from "../../Model/Model";
import {debounce} from "lodash";
import {permissionObj} from "../../../../../Helpers/permission";

/**
 *
 * @Email: kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer
 *
 * Custom hook/component to manage and render a list of geofences with actions.
 *
 * Handles:
 * - Fetching geofences from the API.
 * - Updating status (active/inactive) of a geofence.
 * - Updating hierarchy level of geofences.
 * - Deleting selected geofences.
 * - Filtering geofences by name.
 * - Geolocation initialization.
 * - Table dropdown actions for each geofence.
 *
 * @param {string} searchElement - Current search term to filter geofences.
 * @param {Function} setSearchElement - Function to update the search term.
 *
 * @returns {Object} Methods, state, and handlers to manage geofences cards.
 */
export const GeofencesCardsApp = ({ searchElement, setSearchElement }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [direction, setDirection] = useState();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    // State management
    const [geofences, setGeofences] = useState([]);
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [dayArray, setDayArray] = useState([]);
    const [showActiveInactiveModal, setShowActiveInactiveModal] = useState(false);
    const [selected, setSelected] = useState(null);
    const [modified, setModified] = useState(false);
    const [center, setCenter] = useState(null);
    const elementRef = useRef(null);
    const { getGeofenceList } = useSelector((state) => state?.FineGeofenceSlice);
    const { openMenu, handleMenuToggle, handleClose } = TableFunction({});

    // Observe container height changes
    useEffect(() => {
        if (!elementRef.current) return;

        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const height = entry.contentRect.height;
                entry.target.style.setProperty('--element-height', `${height}px`);
                entry.target.style.setProperty('height', `${height}px`);
            }
        });

        observer.observe(elementRef.current);
        return () => observer.disconnect();
    }, []);

    // Fetch geofence data on mount
    useEffect(() => {
        getData();
    }, []);

    // Filter geofences whenever searchElement or API list changes
    useEffect(() => {
        if (getGeofenceList.length === 0) return;
        if (searchElement === "") {
            setGeofences(GEOFENCES_MODEL.buildGeofenceListMap(getGeofenceList, setDayArray));
        } else {
            const filteredOptions = filterByName(geofences, searchElement);
            setGeofences(filteredOptions);
        }
    }, [searchElement, getGeofenceList]);

    // Refetch data after modifications
    useEffect(() => {
        if (modified) {
            getData();
            setModified(false);
        }
    }, [modified]);

    /**
     * Fetches geofences from API and updates state.
     */
    const getData = () => {
        dispatch(resetGetGeofenceList());
        setSearchElement("");
        dispatch(GetGeofenceList()).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                if(data.length > 0){
                    let aux = GEOFENCES_MODEL.buildGeofenceListMap(data);
                    setGeofences(aux);
                    let center = GEOFENCES_MODEL.getGeofenceCenter(aux[0]);
                    if(center) {
                        setCenter(center);
                        handleMapMove(center[0], center[1])
                    }
                }else{
                    handleMapMove(DEFAULT_LATITUDE, DEFAULT_LONGITUDE)
                }
            }
        });
    };

    const handleMapMove = debounce((lat, lng) => {
        tryGetAddress(lat, lng, dispatch, setDirection);
    }, 800);

    /**
     * Returns JSX for dropdown action buttons for each geofence
     */
    const getActionsButtons = ({ geofence, backgroundColor }) => (
        <div className={`d-flex align-items-center ${backgroundColor}`}>
            <DropDownMenuProfile
                menuId={`menu-${geofence?.geofenceId}`}
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                iconOptions={<IconOptions className={'icon-color-shades-400'} />}
            >
                <DropDownMenuItem
                    handleClose={handleClose}
                    name={t(
                        DEV || permission?.includes(permissionObj?.WEB_TRAFFIC_MANAGE_GEOFENCES_UPDATE)
                            ? 'update'
                            : 'details',
                    )}
                    icon={
                        DEV || permission?.includes(permissionObj?.WEB_TRAFFIC_MANAGE_GEOFENCES_UPDATE) ? (
                            <UpdateIconsOptions />
                        ) : (
                            <DetailsIconsOptions />
                        )
                    }
                    click={() => goUrl(`${routes.geofences.update}${geofence?.geofenceId}`)}
                />
                {(DEV || permission?.includes(permissionObj?.WEB_TRAFFIC_MANAGE_GEOFENCES_DELETE)) && (
                    <DropDownMenuItem
                        handleClose={handleClose}
                        name={t('delete')}
                        fontColor='font-color-error-600'
                        iconColor={'icon-color-error-code-600'}
                        icon={<DeleteIconsOptions />}
                        click={() => {
                            setDeleteModalShow(true);
                            setSelected(geofence);
                        }}
                    />
                )}
            </DropDownMenuProfile>
        </div>
    );

    /**
     * Navigate to a specific URL while preserving tab and subview state.
     */
    const goUrl = (url) =>{
        navigate(url, {
            state: { currentTab: TABS_TRAFFIC.MANAGE.INDEX, currentSubView: TRAFFIC_SUB_VIEW_MODEL.MANAGE.GEOFENCES },
        });
    }
    /**
     * Toggle geofence status (active/inactive)
     */
    const handleUpdateStatus = () => {
        const statusId = selected.statusId === STATUS_MODEL.ACTIVE_COMPANY
            ? STATUS_MODEL.IN_ACTIVE_COMPANY
            : STATUS_MODEL.ACTIVE_COMPANY;

        dispatch(UpdateGeofenceStatus({ id: selected.geofenceId, statusId })).then(
            ({ payload: { data: { success, code } } }) => {
                if (success) {
                    toast.success(t("has_been_updated_successfully"));
                    setModified(true);
                } else errorMsg(code);
            }
        );
    };

    /**
     * Updates hierarchy level of a geofence
     */
    const handleUpdateHierarchyLevel = (data) => {
        dispatch(UpdateGeofenceHierarchyLevel({ id: data.id, hierarchyLevel: parseInt(data.newIndex + 1) }))
            .then(({ payload: { data: { success, code } } }) => {
                if (success) {
                    toast.success(t("has_been_updated_successfully"));
                    getData();
                } else errorMsg(code);
            });
    };

    /**
     * Deletes the selected geofence
     */
    const handleDeleteSelected = () => {
        if (selected?.id) {
            dispatch(DeleteItemsApi({ tableName: TABLES.GEOFENCE, body: [selected?.geofenceId] }))
                .then(({ payload: { data: { success, code } } }) => {
                    if (success) {
                        setModified(true);
                        setSelected(null);
                    } else {
                        errorMsg(code);
                    }
                },
            );
        }
    };


    return {
        DEV,
        permission,
        expanded,
        setExpanded,
        deleteModalShow,
        setDeleteModalShow,
        geofences,
        setGeofences,
        t,
        handleDeleteSelected,
        getActionsButtons,
        dayArray,
        handleUpdateStatus,
        handleUpdateHierarchyLevel,
        showActiveInactiveModal,
        setShowActiveInactiveModal,
        selected, setSelected,
        goUrl,
        center,
        direction,
        handleMapMove,
    };
};
